import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { productsApi } from '../../api/endpoints';
import StatusBadge from '../../components/common/StatusBadge';
import { ProductDetail, ProductStatusMap } from '../../types';
import Select from '../../components/common/Select';
import { FiRefreshCw, FiEdit, FiCheck, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'prices' | 'inventory'>('prices');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncingPrice, setSyncingPrice] = useState(false);
  const [editForm, setEditForm] = useState({
    sku: '',
    ean: '',
    title: '',
    costPrice: '',
    listPrice: '',
    status: 0,
  });

  const fetchProduct = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await productsApi.get(id);
      setProduct(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Blad pobierania produktu');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const startEditing = useCallback(() => {
    if (!product) return;
    setEditForm({
      sku: product.sku || '',
      ean: product.ean || '',
      title: product.title,
      costPrice: product.costPrice?.toString() || '',
      listPrice: product.listPrice?.toString() || '',
      status: product.status,
    });
    setEditing(true);
  }, [product]);

  const handleSave = useCallback(async () => {
    if (!id) return;
    setSaving(true);
    try {
      await productsApi.update(id, {
        sku: editForm.sku || null,
        ean: editForm.ean || null,
        title: editForm.title,
        costPrice: editForm.costPrice ? Number(editForm.costPrice) : null,
        listPrice: editForm.listPrice ? Number(editForm.listPrice) : null,
        status: editForm.status,
      });
      toast.success('Produkt zaktualizowany');
      setEditing(false);
      fetchProduct();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Blad aktualizacji produktu');
    } finally {
      setSaving(false);
    }
  }, [id, editForm, fetchProduct]);

  const handleSyncPricing = useCallback(async () => {
    if (!id) return;
    setSyncingPrice(true);
    try {
      await productsApi.syncPricing(id);
      toast.success('Synchronizacja ceny rozpoczeta');
      fetchProduct();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Blad synchronizacji ceny');
    } finally {
      setSyncingPrice(false);
    }
  }, [id, fetchProduct]);

  const formatCurrency = (amount: number | null) =>
    amount !== null
      ? amount.toLocaleString('pl-PL', { style: 'currency', currency: 'EUR' })
      : '-';

  const formatDate = (dateStr: string | null) =>
    dateStr ? new Date(dateStr).toLocaleDateString('pl-PL') : '-';

  if (loading) {
    return (
      <div className="loader-wrapper">
        <div className="loader" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="page-container">
        <div className="alert alert-danger">Nie znaleziono produktu.</div>
        <Link to="/products" className="btn btn-secondary">
          Powrot do listy
        </Link>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-left">
          <Link to="/products" className="btn btn-link">
            &larr; Produkty
          </Link>
          <h1>{product.title}</h1>
        </div>
        <div className="page-actions">
          {!editing ? (
            <button className="btn btn-secondary" onClick={startEditing}>
              <FiEdit /> Edytuj
            </button>
          ) : (
            <>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                <FiCheck /> {saving ? 'Zapisywanie...' : 'Zapisz'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setEditing(false)}
              >
                <FiX /> Anuluj
              </button>
            </>
          )}
          <button
            className="btn btn-secondary"
            onClick={handleSyncPricing}
            disabled={syncingPrice}
          >
            <FiRefreshCw className={syncingPrice ? 'spin' : ''} />
            {syncingPrice ? 'Synchronizacja...' : 'Synchronizuj cene'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 auto' }}>
          {editing ? (
            <div className="card">
              <div className="card-header">
                <h3>Edycja produktu</h3>
              </div>
              <div className="card-body">
                <div className="form-group">
                  <label>SKU</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editForm.sku}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, sku: e.target.value }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>EAN</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editForm.ean}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, ean: e.target.value }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Tytul</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editForm.title}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, title: e.target.value }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Cena kosztu</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    value={editForm.costPrice}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        costPrice: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Cena katalogowa</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    value={editForm.listPrice}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        listPrice: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <Select
                    options={Object.entries(ProductStatusMap).map(([key, label]) => ({ value: key, label }))}
                    value={String(editForm.status)}
                    onChange={(value) =>
                      setEditForm((prev) => ({
                        ...prev,
                        status: Number(value),
                      }))
                    }
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">ASIN</span>
                <span className="detail-value">{product.asin}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">SKU</span>
                <span className="detail-value">{product.sku || '-'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">EAN</span>
                <span className="detail-value">{product.ean || '-'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">ID zewnetrzny</span>
                <span className="detail-value">{product.externalId || '-'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Tytul</span>
                <span className="detail-value">{product.title}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Opis</span>
                <span className="detail-value">{product.description || '-'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Marka</span>
                <span className="detail-value">{product.brand || '-'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Kategoria</span>
                <span className="detail-value">{product.category || '-'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Cena kosztu</span>
                <span className="detail-value">{formatCurrency(product.costPrice)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Cena katalogowa</span>
                <span className="detail-value">{formatCurrency(product.listPrice)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Waluta</span>
                <span className="detail-value">{product.currency}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Status</span>
                <span className="detail-value">
                  <StatusBadge type="product" value={product.status} />
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Kanal</span>
                <span className="detail-value">
                  <StatusBadge type="channel" value={product.channel} />
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Ostatnia synchronizacja</span>
                <span className="detail-value">{formatDate(product.lastSyncedAt)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Utworzono</span>
                <span className="detail-value">{formatDate(product.createdAt)}</span>
              </div>
            </div>
          )}
        </div>

        {product.imageUrl && (
          <div className="card" style={{ flex: '0 0 250px', alignSelf: 'flex-start' }}>
            <div className="card-body" style={{ textAlign: 'center' }}>
              <img
                src={product.imageUrl}
                alt={product.title}
                style={{ maxWidth: '100%', maxHeight: '250px', objectFit: 'contain' }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="tabs" style={{ marginTop: '1.5rem' }}>
        <button
          className={`tab ${activeTab === 'prices' ? 'active' : ''}`}
          onClick={() => setActiveTab('prices')}
        >
          Ceny ({product.prices.length})
        </button>
        <button
          className={`tab ${activeTab === 'inventory' ? 'active' : ''}`}
          onClick={() => setActiveTab('inventory')}
        >
          Stany magazynowe ({product.inventoryRecords.length})
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'prices' && (
          <div className="table-responsive">
            {product.prices.length === 0 ? (
              <p className="text-muted">Brak danych cenowych.</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Typ ceny</th>
                    <th>Kwota</th>
                    <th>Waluta</th>
                    <th>Obowiazuje od</th>
                    <th>Obowiazuje do</th>
                  </tr>
                </thead>
                <tbody>
                  {product.prices.map((price) => (
                    <tr key={price.id}>
                      <td>{price.priceType}</td>
                      <td>{formatCurrency(price.amount)}</td>
                      <td>{price.currency}</td>
                      <td>{formatDate(price.effectiveFrom)}</td>
                      <td>{formatDate(price.effectiveTo)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'inventory' && (
          <div className="table-responsive">
            {product.inventoryRecords.length === 0 ? (
              <p className="text-muted">Brak rekordow magazynowych.</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Magazyn</th>
                    <th>Dostepne</th>
                    <th>Zarezerwowane</th>
                    <th>W transporcie</th>
                    <th>Mozliwe do realizacji</th>
                    <th>Backorder</th>
                    <th>Synchronizacja</th>
                  </tr>
                </thead>
                <tbody>
                  {product.inventoryRecords.map((rec) => (
                    <tr key={rec.id}>
                      <td>{rec.warehouseName || '-'}</td>
                      <td>{rec.availableQuantity}</td>
                      <td>{rec.reservedQuantity ?? '-'}</td>
                      <td>{rec.inboundQuantity ?? '-'}</td>
                      <td>{rec.totalFulfillableQuantity ?? '-'}</td>
                      <td>
                        {rec.isBackordered ? (
                          <span className="badge badge-danger">Tak</span>
                        ) : (
                          <span className="badge badge-success">Nie</span>
                        )}
                      </td>
                      <td>{formatDate(rec.syncedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailPage;
